import { safeStorage } from "../../utils/storage";
import { getBrowserTimeZone } from "../../utils/timeZones";
import { isValidTimeZone } from "../../utils/zonedTime";
import {
  DEFAULT_BUSINESS_TYPE,
  DEFAULT_DISCOUNT,
  DEFAULT_STAMP_COUNT,
  getBusinessType,
} from "./presets";

// The Customize answers. Kept on the phone as they are typed, so an in-app
// browser reload (Instagram reloads the page when you switch apps) does not lose
// them; once the card exists on the server it is the source of truth instead.

// Keyed by Firebase uid: a second account in the same browser starts clean.
export const draftKey = (uid) => `start:draft:${uid || "anon"}`;
export const logoKey = (uid) => `start:logo:${uid || "anon"}`;

export function browserTimeZone() {
  const zone = getBrowserTimeZone();
  return zone && zone !== "Etc/Unknown" && isValidTimeZone(zone) ? zone : null;
}

export function emptyBack() {
  return {
    instagram: { enabled: false, value: "" },
    booking: { enabled: false, value: "", label: "" },
    hours: { enabled: false, value: "" },
    info: { enabled: false, label: "", value: "" },
  };
}

// `presetText(key)` resolves start.presets.<type>.<key> in the current language.
export function defaultDraft({ businessType = DEFAULT_BUSINESS_TYPE, presetText } = {}) {
  const type = getBusinessType(businessType);
  return {
    version: 1,
    storeName: "",
    businessType: type.id,
    businessTypeChosen: false,
    cardColor: type.color,
    iconId: type.icon,
    cardType: "stamp",
    reward: presetText ? presetText(type.id, "reward") : "",
    stampCount: DEFAULT_STAMP_COUNT,
    midReward: {
      enabled: false,
      position: Math.floor(DEFAULT_STAMP_COUNT / 2),
      label: presetText ? presetText(type.id, "milestone") : "",
    },
    discount: DEFAULT_DISCOUNT,
    couponOffer: presetText ? presetText(type.id, "reward") : "",
    back: emptyBack(),
    form: { phone: false, birthday: false, marketing: true },
    currency: null,
    country: null,
    paperSize: "a4",
    timeZone: browserTimeZone(),
    // "monogram" until a logo is uploaded; the initials logo is then redrawn
    // whenever the name or colour changes.
    logoKind: "monogram",
    logoUploaded: false,
    monogramSignature: null,
    savedStripSignature: null,
    savedLookSignature: null,
    // Fields the merchant has typed into, so a business-type switch only
    // replaces example text, never their own.
    touched: {},
  };
}

export function loadDraft(uid) {
  const value = safeStorage.get(draftKey(uid));
  return value && typeof value === "object" && value.version === 1 ? value : null;
}

export function saveDraft(uid, draft) {
  safeStorage.set(draftKey(uid), draft);
}

export function loadLogo(uid) {
  const value = safeStorage.get(logoKey(uid));
  return value && typeof value === "object" && value.dataUrl ? value : null;
}

export function saveLogo(uid, logo) {
  if (logo) safeStorage.set(logoKey(uid), logo);
  else safeStorage.remove(logoKey(uid));
}

// Rebuilds the answers from what is already saved (a different phone, or
// cleared storage). Everything restored counts as the merchant's own answer:
// the example text of a business type must never overwrite it.
export function draftFromServer({ store, pass, form, base }) {
  const draft = { ...base, touched: { ...(base.touched || {}) } };
  const restored = (field) => {
    draft.touched[field] = true;
  };
  const data = pass?.pass;
  const meta = data?.onboarding || {};

  if (store) {
    draft.storeId = store.id;
    draft.storeName = store.name || draft.storeName;
    if (store.background_color) {
      draft.cardColor = store.background_color;
      restored("cardColor");
    }
    if (store.currency) draft.currency = store.currency;
    if (store.time_zone) draft.timeZone = store.time_zone;
    if (store.logo_url) {
      if (meta.logo_kind === "monogram") {
        draft.logoKind = "monogram";
        // Matches the signature save() compares, so an unchanged initials logo
        // is not uploaded again.
        draft.monogramSignature = `${store.name}|${store.background_color}|${store.primary_color}`;
      } else {
        // An uploaded logo (or an older card without the marker): keep it.
        draft.logoKind = "upload";
        draft.logoUploaded = true;
      }
    }
  }

  if (data) {
    const type = data.type || "stamp";
    draft.cardType = type;
    if (meta.business_type) {
      draft.businessType = meta.business_type;
      draft.businessTypeChosen = true;
    }
    if (meta.icon_id) {
      draft.iconId = meta.icon_id;
      restored("iconId");
    }
    if (type === "stamp" && Array.isArray(data.milestones) && data.milestones.length) {
      const sorted = [...data.milestones].sort((a, b) => Number(a.position) - Number(b.position));
      const final = sorted[sorted.length - 1];
      if (final.label) {
        draft.reward = final.label;
        restored("reward");
      }
      draft.stampCount = Number(final.position) || Number(data.stamp_count) || draft.stampCount;
      if (sorted.length > 1) {
        draft.midReward = { enabled: true, position: Number(sorted[0].position), label: sorted[0].label || "" };
        restored("midReward");
      }
    }
    if (type === "discount" && data.discount) draft.discount = Number(data.discount);
    if (type === "coupon" && data.campaign?.value) {
      draft.couponOffer = data.campaign.value;
      restored("couponOffer");
    }
    if (meta.back && typeof meta.back === "object") {
      draft.back = { ...emptyBack(), ...meta.back };
    }
  }

  const fields = form?.content?.fields;
  if (Array.isArray(fields)) {
    draft.form = {
      phone: fields.some((f) => f.field === "phone"),
      birthday: fields.some((f) => f.field === "birthday"),
      marketing: fields.some((f) => f.field === "marketing"),
    };
  }
  return draft;
}
